import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Icon } from '../index';

import { setStateOf, type DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { useTransientTip } from './useTransientTip';
import { stateAria } from "../a11y";

// Cells sit 4px apart (row gap s.s2); a full 8px hitSlop halo would overlap the
// adjacent cell by 12px (notably − beside +, a destructive trim). Restore the
// ≥44 touch target on the VERTICAL axis only — horizontal reach is the flex width.
const ROW_SLOP = { top: 8, bottom: 8 } as const;

// SetCountRow (timeline logger) — a compact STEPPER for the active exercise's
// set count: `‹ N / total ›  −  +` (Antenore 2026-06-01, inbox 107794f1). It
// replaced the row of one-pill-per-set, which overflowed past ~5 sets with no
// scroll cue. The stepper is fixed-width and works for any set count.
//
//  - `‹` / `›` move the bound set (prev / next), disabled at the ends — so the
//    arrows themselves signal where you are in the sequence. They reach
//    done/failed sets too, binding them for editing (slice 5).
//  - The current number is colour-coded by that set's state (done / pr /
//    failed / pending); `/ total` tells you how many sets there are.
//  - `−` drops the trailing set (kept available even once every planned set is
//    logged, so an over-logged set can be trimmed — explicit tap, never silent
//    destruction); `+` adds one. Both act on this exercise only, in a
//    superset too (#387); a whole round is in the superset menu.
//  - LG-77 — the insert glyph puts a new set BEFORE the one the stepper is
//    pointing at, which is how a warm-up gets in front of set 1 and how a
//    preparation set gets between N and N+1 (Antenore 2026-09-08: «inserire
//    tra set è utile finché non ho eseguito il set»).
//
//    ⭐ Why a second button and not a mode on ＋. The first shape considered
//    was a before/after toggle: ＋ would append or insert depending on a
//    switch. That is a MODE, and it fails where this row lives — a control
//    pressed without looking, between sets, tired — with a silent error: the
//    set lands in the wrong place and you find out later. The cost is also
//    asymmetric, since nearly every tap here is "append", so one forgotten
//    flip misplaces every set after it. Two buttons that each do one thing
//    always need no memory and no state. What his idea got RIGHT, and what
//    this keeps, is the anchor: `‹ N/total ›` is already on screen, so
//    "before the one shown" needs no explaining.
//
//    ⛔ It is ABSENT, never disabled, once the shown set is closed — the PE-26
//    rule. And the reason is the timeline, not tidiness: a completed set
//    carries a real `completedAt`, so putting a set in front of one already
//    logged would claim the athlete lifted in an order they did not. Insert
//    is offered on what has not happened yet; nothing here ever reorders.
//
// The at-a-glance per-set overview the pills gave now lives in the roster
// (logged/planned count) and the timeline feed (every logged set), so the
// FocusPanel can afford the compact stepper.
export function SetCountRow({
  sets,
  activeSetIdx,
  onSelect,
  onAdd,
  onRemove,
  onInsertBefore,
  canRemove,
  editLocked,
  theme,
}: {
  sets: DraftSet[];
  /** Index of the set FocusPanel currently binds, or null when none pending. */
  activeSetIdx: number | null;
  onSelect: (idx: number) => void;
  onAdd: () => void;
  onRemove: () => void;
  /** LG-77 — insert a set before `setIdx`. The index comes from HERE rather
   *  than from the caller because this row already owns the pointer the
   *  gesture is relative to; asking the parent to recompute it would be a
   *  second answer to "which set is shown" that could drift from this one. */
  onInsertBefore: (setIdx: number) => void;
  /** False disables −: the entry's last remaining set is never removable. */
  canRemove: boolean;
  /** Editing a past session: ＋ add-set is forbidden (greyed + balloon). The
   *  arrows (navigate) and − (remove a logged set) stay live. §9 edit-scope. */
  editLocked: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { showTip, tipNode } = useTransientTip(theme);

  const count = sets.length;
  // The pointer shown by the stepper. When a set is bound, point at it; when
  // every set is logged and none is bound (live, all-done), point at the last
  // set so the arrows can still walk back into the logged sets to edit them.
  const currentIdx =
    activeSetIdx !== null && activeSetIdx >= 0
      ? activeSetIdx
      : count > 0
        ? count - 1
        : 0;
  const canPrev = currentIdx > 0;
  const canNext = currentIdx < count - 1;
  const currentState = count > 0 ? setStateOf(sets[currentIdx]!) : 'pending';
  // Offered only on a set that has NOT been performed. `completedAt` and not
  // `currentState`, because the question is literally "did this happen yet",
  // and a state name is one derivation away from that fact.
  const canInsert =
    !editLocked && count > 0 && sets[currentIdx]!.completedAt === null;

  const addLockMsg = t('mobile.timeline.addSetLocked', {
    defaultValue:
      "Editing a past session: you can't add sets. Fix the values or remove a set instead.",
  });

  return (
    <>
      {tipNode}
      <View style={styles.row}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canPrev }}
        {...stateAria({ disabled: !canPrev })}
        accessibilityLabel={t('mobile.timeline.prevSet', {
          defaultValue: 'Previous set',
        })}
        disabled={!canPrev}
        onPress={() => onSelect(currentIdx - 1)}
        hitSlop={ROW_SLOP}
        style={({ pressed }) => [
          styles.arrow,
          !canPrev && styles.arrowDisabled,
          pressed && styles.pressed,
        ]}
      >
        <Text style={[styles.arrowText, !canPrev && styles.arrowTextDisabled]}>
          ‹
        </Text>
      </Pressable>

      <View
        style={styles.readout}
        accessibilityRole="text"
        // The number's colour carries the set's state (done / pr / failed /
        // pending) — a channel a screen reader can't see. `setState.*` says
        // both position AND state in one sentence, so the label never reduces
        // to the colour-only half. Same key family the per-set pills used
        // before the stepper replaced them.
        accessibilityLabel={t(`mobile.timeline.setState.${currentState}`, {
          n: currentIdx + 1,
          total: count,
          // The fallback carries the state too: a fallback that dropped it
          // would silently reopen the colour-only hole this label closes.
          defaultValue: `Set ${currentIdx + 1} of ${count}, ${currentState}`,
        })}
      >
        <Text style={[styles.current, styles[`current_${currentState}`]]}>
          {currentIdx + 1}
        </Text>
        <Text style={styles.total}>/{count}</Text>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canNext }}
        {...stateAria({ disabled: !canNext })}
        accessibilityLabel={t('mobile.timeline.nextSet', {
          defaultValue: 'Next set',
        })}
        disabled={!canNext}
        onPress={() => onSelect(currentIdx + 1)}
        hitSlop={ROW_SLOP}
        style={({ pressed }) => [
          styles.arrow,
          !canNext && styles.arrowDisabled,
          pressed && styles.pressed,
        ]}
      >
        <Text style={[styles.arrowText, !canNext && styles.arrowTextDisabled]}>
          ›
        </Text>
      </Pressable>

      {/* LG-77 — the insert cell OCCUPIES the gap unit rather than adding a
          seventh one. The row is a 6-unit grid whose columns line up with the
          rows above and below (see makeStyles), so a new cell beside − would
          shift ＋ off the right-hand unit and skew the whole card. Antenore's
          own proposal put it here, in the empty space, and the grid is the
          reason that was the right instinct. When it is absent the gap comes
          back, so the layout is identical to before on a closed set. */}
      {canInsert ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.timeline.insertSetBefore', {
            n: currentIdx + 1,
            defaultValue: 'Insert a set before set {{n}}',
          })}
          onPress={() => onInsertBefore(currentIdx)}
          hitSlop={ROW_SLOP}
          style={({ pressed }) => [styles.ctrl, pressed && styles.pressed]}
        >
          {/* Same size and tone as the ＋/− glyphs beside it (ctrlText:
              sizes.lg, ink2). The first cut drew it at sizes.md and the UX
              audit measured the result: in a row where every sibling is 20px
              at weight 700-800, a 16px stroke icon reads as lighter and drops
              out of the glance. */}
          <Icon
            name="between-horizontal-start"
            size={theme.typography.sizes.lg}
            color={theme.palette.ink2}
          />
        </Pressable>
      ) : null}
      {/* Whatever the insert cell did not take. Nothing to draw when − is
          present and the insert took the only gap unit. */}
      {canInsert && canRemove ? null : (
        <View
          style={[
            styles.spacer,
            !canRemove && !canInsert && styles.spacerWide,
          ]}
        />
      )}

      {canRemove ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.timeline.removeSet', {
            defaultValue: 'Remove set',
          })}
          onPress={onRemove}
          hitSlop={ROW_SLOP}
          style={({ pressed }) => [styles.ctrl, pressed && styles.pressed]}
        >
          <Text style={styles.ctrlText}>−</Text>
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: editLocked }}
        {...stateAria({ disabled: editLocked })}
        accessibilityLabel={t('mobile.timeline.addSet', {
          defaultValue: 'Add set',
        })}
        onPress={editLocked ? () => showTip(addLockMsg) : onAdd}
        hitSlop={ROW_SLOP}
        style={({ pressed }) => [
          styles.ctrl,
          editLocked && styles.ctrlLocked,
          pressed && !editLocked && styles.pressed,
        ]}
      >
        <Text style={[styles.ctrlText, editLocked && styles.ctrlTextLocked]}>
          +
        </Text>
      </Pressable>
      </View>
    </>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  // Tap targets sized for reduced vision ([[feedback_user_visual_accessibility]]).
  // Compacted to 36 (oversized-UI shrink) with hitSlop={ROW_SLOP} on each Pressable so
  // the touch target stays ≥44; every cell is flex:1 = one unit of the shared
  // 6-unit card grid (‹ · n/tot · › · gap · − · +), columns line up with the
  // rows above and below.
  const size = 36;
  return StyleSheet.create({
    row: { flexDirection: 'row', alignItems: 'center', gap: s.s2 },
    arrow: {
      flex: 1,
      height: size,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    arrowDisabled: { opacity: 0.4 },
    arrowText: { color: p.ink, fontSize: ty.sizes.lg, fontWeight: '800' },
    arrowTextDisabled: { color: p.ink3 },
    pressed: { backgroundColor: p.surface2 },
    // The current/total readout = one grid cell between the arrows.
    readout: {
      flex: 1,
      height: size,
      flexDirection: 'row',
      // center, not baseline: with mixed font sizes (current 20 / total 12)
      // baseline pushed the number low so it sat below the arrow + ＋/−
      // glyphs on the same row (Antenore 2026-06-02 — "il numero del set non
      // è allineato centralmente ai bottoni della stessa riga").
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
    },
    current: {
      fontSize: ty.sizes.lg,
      fontWeight: '800',
      fontVariant: ['tabular-nums'],
      color: p.ink3,
    },
    current_pending: { color: p.ink },
    current_done: { color: p.complete },
    current_pr: { color: p.pr },
    current_failed: { color: p.danger },
    total: {
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
      color: p.ink3,
    },
    // Gap cell between the ‹n/tot› stepper and the −/+ pair. One unit normally
    // (− at unit 5, + at unit 6); two units when − is hidden so + stays at the
    // far-right unit 6, keeping the grid aligned either way.
    spacer: { flex: 1 },
    spacerWide: { flex: 2 },
    ctrl: {
      flex: 1,
      height: size,
      borderRadius: r.r2,
      borderWidth: 1,
      // Same border weight as the ‹/› arrows so the 6-cell grid reads uniform
      // (Antenore 2026-07-02: −/+ looked bigger than the other buttons; the
      // fullwidth ＋ glyph + the stronger border were the cause, box is 36 like
      // the rest).
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    ctrlText: { color: p.ink2, fontSize: ty.sizes.lg, fontWeight: '700' },
    // Edit-locked ＋: greyed + muted glyph (same posture as the disabled
    // arrows). Stays Pressable so the tap surfaces the lock balloon.
    ctrlLocked: { opacity: 0.4 },
    ctrlTextLocked: { color: p.ink3 },
  });
}
